import AppCheckbox from 'components/FormElements/AppCheckBox';
import InteractiveTable, {
  Column as TableColumn,
} from 'components/InteractiveTable';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ActionRefLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  dateToLabel,
  CalendarDay,
} from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';

import { useState } from 'react';
import Review from './Review';
import Button from 'components/Button';
import useSplitReconcilableTransactions, {
  ReconcilableTransaction,
} from 'hooks/lp/ui/bankReconciliation/useSplitReconcilableTransactions';
import SplitTransactionDialog from './SplitTransactionDialog';
import { Box, styled, Tooltip } from '@mui/material';
import { FastForward } from '@mui/icons-material';
import ErrorBoundary from 'components/ErrorBoundary';
import { isAfter } from 'date-fns';
import SearchField from 'further-ui/components/SearchField';
import { Column } from 'further-ui/components/Layout';

const SplitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs),
}));

const StyledFastForward = styled(FastForward)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.text.darkBodyText,
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(theme.spacingSizes.xxs),
}));

type Props = {
  vehicleId: string;
  bankAccountId: string;
  preselectedTransactionId?: string;
  defaultReconcilableTransactions: Array<ReconcilableTransaction>;
  bankOperation: {
    id: string;
    spent?: number;
    received?: number;
    accountCurrency: CurrencyCode;
    date: CalendarDay;
  };
  onClose: () => void;
};

const FindAndMatch: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  preselectedTransactionId,
  bankOperation,
  onClose,
  defaultReconcilableTransactions,
}) => {
  const componentId = `find-and-match-${bankOperation.id}`;
  const bankOperationAmountTcy =
    bankOperation.spent ?? bankOperation.received ?? 0;
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    Array<string>
  >(preselectedTransactionId ? [preselectedTransactionId] : []);

  const { filters, handleChangeFilters } = useFilters(
    `find-and-match-${bankOperation.id}`,
    {
      defaultFilters: {
        pastOnly: false,
        receivedOnly: !!bankOperation.received,
        spentOnly: !!bankOperation.spent,
        searchQuery: '',
      },
    },
  );

  const {
    reconcilableTransactions,
    transactionToSplit,
    selectTransactionToSplit,
    showSplitTransactionDialog,
    closeSplitTransactionDialog,
    splitSelectedTransaction,
  } = useSplitReconcilableTransactions(
    defaultReconcilableTransactions,
    (splitFromTransactionId, newTransactionId) => {
      setSelectedTransactionIds([
        ...selectedTransactionIds.filter((id) => id !== splitFromTransactionId),
        newTransactionId,
      ]);
    },
  );

  const checkOperationType = (transaction: ReconcilableTransaction) =>
    (filters.receivedOnly &&
      transaction.operationType === ActionOperationType.Received) ||
    (filters.spentOnly &&
      transaction.operationType === ActionOperationType.Spent) ||
    (!filters.receivedOnly && !filters.spentOnly);

  const checkDate = (transaction: ReconcilableTransaction) =>
    !filters.pastOnly || transaction.ledgerDate.toUTCDate() <= new Date();

  const searchByQuery = (transaction: ReconcilableTransaction) =>
    !filters.searchQuery ||
    transaction.counterpartyLabel
      .toLowerCase()
      .includes(filters.searchQuery.toLowerCase()) ||
    transaction.unreconciledAmountTcy === Number(filters.searchQuery);

  const reconcilableTransactionsToDisplay = reconcilableTransactions
    .filter(checkOperationType)
    .filter(checkDate)
    .filter(searchByQuery);

  const selectedTransactions = reconcilableTransactionsToDisplay.filter(
    (transaction) => selectedTransactionIds.includes(transaction.id),
  );

  const handleTransactionSelection = (
    selectedTransactionIds: Array<string>,
  ) => {
    setSelectedTransactionIds(selectedTransactionIds);
  };

  const columns: Array<TableColumn> = [
    {
      field: 'ledgerDate',
      headerName: 'Date',
      renderCell: ({ row }) => {
        const isFutureDate = isAfter(
          row.ledgerDate.endOfDay(),
          bankOperation.date.endOfDay(),
        );

        return (
          <StyledBox>
            {dateToLabel(row.ledgerDate)}
            {isFutureDate && (
              <Tooltip title="This action has a ledger date in the future and selecting it will generate a prepayment transaction">
                <StyledFastForward />
              </Tooltip>
            )}
          </StyledBox>
        );
      },
      flex: 1,
    },
    {
      field: 'actionRef',
      headerName: 'Action',
      renderCell: ({ row }) => ActionRefLabels[row.actionRef],
      flex: 1,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      flex: 1,
    },
    {
      field: 'spent',
      headerName: 'Spent',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Spent
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
      flex: 1,
    },
    {
      field: 'received',
      headerName: 'Received',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Received
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
      flex: 1,
    },
    {
      field: 'split',
      headerName: '',
      renderCell: ({ row }) =>
        row.id &&
        !row.splitFromTransactionId &&
        row.actionRef !== ActionRef.BankAccountTransfer &&
        selectedTransactionIds.includes(row.id) ? (
          <SplitButton
            variant="outlined"
            color="primary"
            size="large"
            fullWidth
            onClick={() => {
              if (row.id) {
                selectTransactionToSplit(row.id);
              }
            }}
          >
            Split
          </SplitButton>
        ) : null,
      flex: 1,
    },
  ];

  return (
    <Column spacing="md">
      <PageFilters>
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            name="searchQuery"
            placeholder="Search by counterparty or amount"
            fullWidth
            onSearch={(value) => handleChangeFilters({ searchQuery: value })}
            allowEmptyQuery
            iconPlacement="left"
            value={filters.searchQuery}
          />
        </Filter>
        <Filter>
          <AppCheckbox
            label="Past transactions only"
            key="pastOnly"
            checked={!!filters.pastOnly}
            onChange={(event) => {
              handleChangeFilters({
                pastOnly: event.target.checked,
              });
            }}
          />
          {bankOperation.received && (
            <AppCheckbox
              label="Show received items only"
              key="receivedOnly"
              checked={!!filters.receivedOnly}
              onChange={(event) => {
                handleChangeFilters({
                  receivedOnly: event.target.checked,
                });
              }}
            />
          )}
          {bankOperation.spent && (
            <AppCheckbox
              label="Show spent items only"
              key="spentOnly"
              checked={!!filters.spentOnly}
              onChange={(event) => {
                handleChangeFilters({
                  spentOnly: event.target.checked,
                });
              }}
            />
          )}
        </Filter>
      </PageFilters>
      <InteractiveTable
        key={JSON.stringify([
          ...reconcilableTransactionsToDisplay,
          ...selectedTransactionIds,
        ])}
        selectable
        onRowSelection={handleTransactionSelection}
        selectedRows={selectedTransactionIds}
        id={componentId}
        columns={columns}
        rows={reconcilableTransactionsToDisplay}
        disableSorting
        disableTableConfiguration
        fullHeight
      />
      {!!selectedTransactions.length && (
        <ErrorBoundary>
          <Review
            vehicleId={vehicleId}
            bankAccountId={bankAccountId}
            bankOperation={bankOperation}
            selectedTransactions={selectedTransactions}
            onClose={onClose}
          />
        </ErrorBoundary>
      )}
      {showSplitTransactionDialog && (
        <SplitTransactionDialog
          onClose={closeSplitTransactionDialog}
          bankOperationAmountTcy={Math.abs(bankOperationAmountTcy)}
          transactionUnreconciledAmountTcy={
            transactionToSplit?.unreconciledAmountTcy ?? 0
          }
          onSplit={splitSelectedTransaction}
          currency={
            transactionToSplit?.currencyCode ?? bankOperation.accountCurrency
          }
        />
      )}
    </Column>
  );
};

export default FindAndMatch;
